import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowsLeftRight, Lightning, Play, CheckCircle, XCircle, Warning,
  MapPin, Package, Clock, Sparkle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { wsEventsUrl } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:     { label: 'Pending',     color: 'bg-muted0' },
  in_progress: { label: 'In progress', color: 'bg-blue-500' },
  completed:   { label: 'Completed',   color: 'bg-emerald-500' },
  cancelled:   { label: 'Cancelled',   color: 'bg-rose-500' },
};

const TRIGGER_META = {
  auto_min_max: { label: 'Auto',    icon: Sparkle },
  manual:       { label: 'Manual',  icon: Lightning },
  manual_scan:  { label: 'Scan',    icon: Lightning },
};

export default function Replenishments() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [scanning, setScanning] = useState(false);
  const [liveTick, setLiveTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const [list, sum] = await Promise.all([
        axios.get(`${API}/api/replenishments`, { params, withCredentials: true }),
        axios.get(`${API}/api/replenishments/summary`, { withCredentials: true }),
      ]);
      setItems(list.data.items || []);
      setSummary(sum.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load replenishments');
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  // Live updates — listen for stock-consuming events and refresh the list
  useEffect(() => {
    let ws, timer;
    const connect = () => {
      try {
        ws = new WebSocket(wsEventsUrl());
        ws.onmessage = (ev) => {
          let evt;
          try { evt = JSON.parse(ev.data); } catch { return; }
          const t = evt?.type;
          if (t && ['pick_completed', 'order_shipped', 'inventory_transferred'].includes(t)) {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => { load(); setLiveTick((n) => n + 1); }, 1500);
          }
        };
        ws.onclose = () => { setTimeout(connect, 5000); };
      } catch (_) { /* noop */ }
    };
    connect();
    return () => { if (timer) clearTimeout(timer); if (ws) { ws.onclose = null; try { ws.close(); } catch (_) { /* noop */ } } };
  }, [load]);

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${API}/api/replenishments/scan`, {}, { withCredentials: true });
      toast.success(`Scan complete: ${data.created} new tasks, ${data.skipped} skipped (${data.scanned} total)`, {
        description: <span data-testid="replenish-scan-toast">{`created=${data.created} skipped=${data.skipped}`}</span>,
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Scan failed'); }
    finally { setScanning(false); }
  };

  const act = async (id, action) => {
    try {
      await axios.post(`${API}/api/replenishments/${id}/${action}`, {}, { withCredentials: true });
      toast.success(`Task ${action}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${action} failed`); }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="replenishments-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b-2 border-slate-900 pb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Operations</div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              Replenishments
              {liveTick > 0 && (
                <span data-testid="replenishments-live" className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-600" title="Live from operations event stream">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                </span>
              )}
            </h1>
            <p className="text-xs text-muted-foreground mt-1">Move stock from bulk into fixed pick locations before they drop below min. Auto-created from Fixed Locations rules + pick events.</p>
          </div>
          <Button
            data-testid="replenishments-scan-btn"
            disabled={scanning}
            onClick={scanNow}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-none font-bold"
          >
            <Lightning size={16} weight="fill" className="mr-1" /> {scanning ? 'Scanning…' : 'Scan now'}
          </Button>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['pending', 'in_progress', 'completed', 'cancelled'].map((s) => (
            <button
              key={s}
              data-testid={`replenish-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`p-3 border text-left transition-all ${filter === s ? 'border-slate-900 bg-muted' : 'border-border bg-card hover:border-slate-400'}`}
            >
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{STATUS_META[s].label}</div>
              <div className="text-2xl font-bold font-mono">{summary[s] ?? 0}</div>
            </button>
          ))}
        </div>
        <div className="flex gap-2 text-xs">
          <button data-testid="replenish-filter-all" onClick={() => setFilter('all')} className={`px-2 py-1 border ${filter === 'all' ? 'border-slate-900 bg-muted' : 'border-border'}`}>All</button>
          <span className="text-muted-foreground self-center ml-auto">Showing: {filter.replace('_', ' ')}</span>
        </div>

        {/* Task list */}
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted border-b border-border">
              <tr>
                {['Task', 'Trigger', 'SKU', 'Variant', 'From', 'To', 'Qty', 'Status', 'Triggered', ''].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">
                  No <span className="font-bold">{filter.replace('_', ' ')}</span> tasks. Try another filter or hit <span className="font-bold">Scan now</span>.
                </td></tr>
              )}
              {items.map((t) => {
                const meta = STATUS_META[t.status] || { label: t.status, color: 'bg-muted0' };
                const trig = TRIGGER_META[t.trigger] || { label: t.trigger, icon: Clock };
                const TIcon = trig.icon;
                const triggered = t.triggered_at ? new Date(t.triggered_at).toLocaleString() : '—';
                return (
                  <tr key={t.id} data-testid={`replenish-row-${t.task_number}`} className="hover:bg-muted">
                    <td className="px-3 py-2 font-mono text-xs font-bold">{t.task_number}</td>
                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground"><TIcon size={12} weight="bold" />{trig.label}</span></td>
                    <td className="px-3 py-2 font-mono font-bold">{t.sku_code || '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {[t.variant_attrs?.size, t.variant_attrs?.color, t.variant_attrs?.style].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{t.from_location_code || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs font-bold">→ {t.to_location_code || '—'}</td>
                    <td className="px-3 py-2 font-mono font-bold text-emerald-700">{t.quantity}</td>
                    <td className="px-3 py-2"><Badge className={`${meta.color} text-white rounded-none text-[10px]`}>{meta.label.toUpperCase()}</Badge></td>
                    <td className="px-3 py-2 text-[10px] text-muted-foreground font-mono">{triggered}</td>
                    <td className="px-3 py-2 flex items-center gap-1 justify-end">
                      {t.status === 'pending' && (
                        <>
                          <Button size="sm" className="rounded-none h-7 text-[10px] bg-brand hover:bg-brand/90 text-white" onClick={() => act(t.id, 'start')} data-testid={`replenish-start-${t.task_number}`}>
                            <Play weight="fill" size={12} className="mr-1" /> Start
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] text-rose-600" onClick={() => act(t.id, 'cancel')} data-testid={`replenish-cancel-${t.task_number}`}>
                            <XCircle weight="bold" size={12} className="mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                      {t.status === 'in_progress' && (
                        <>
                          <Button size="sm" className="rounded-none h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(t.id, 'complete')} data-testid={`replenish-complete-${t.task_number}`}>
                            <CheckCircle weight="fill" size={12} className="mr-1" /> Complete
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] text-rose-600" onClick={() => act(t.id, 'cancel')} data-testid={`replenish-cancel-${t.task_number}`}>
                            <XCircle weight="bold" size={12} className="mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
